'use strict';

const urlLib = require('url');

const validator = require('validator');

const errorMessages = require('./error-messages.json');

/** @exports lib/helpers */
const helpers = {};

/**
 * Return URL object if url is valid
 *
 * @throws error if url is malformed
 * @param {string} url - URL to check
 * @param {string} [errorMessage=errorMessages.malformedUrl] - message of error
 * if thrown
 * @returns {URL} parsed URL object if url is valid
 */
helpers.validateURL = (url, errorMessage = errorMessages.malformedUrl) => {
  /* eslint-disable node/prefer-global/url */
  let URLConstructor;
  if (typeof URL === 'function') {
    URLConstructor = URL;
  } else if (urlLib && urlLib.URL) {
    URLConstructor = urlLib.URL;
  } else {
    throw new ReferenceError('ReferenceError: URL is not defined');
  }
  /* eslint-enable */

  try {
    return new URLConstructor(url);
  } catch {
    throw new Error(errorMessage);
  }
};

/**
 * Allow localhost URIs with no tld for http and https.
 *
 * @param {URL} url - URI to check
 * @returns {boolean} whether or not url is an allowed HTTP URI (localhost)
 */
helpers.isLocalhost = url =>
  validator.isURL(url.href, {
    /* eslint-disable camelcase */
    protocols: ['http', 'https'],
    require_protocol: true,
    require_tld: false,
    host_whitelist: ['localhost'],
    /* eslint-enable */
  });

/**
 * Check if url is valid URI with protocol HTTPS
 *
 * @param {URL} url - URL to check
 * @returns {boolean} whether or not url is valid w/ protocol HTTPS
 */
helpers.isHTTPS = url =>
  validator.isURL(url.href, {
    /* eslint-disable camelcase */
    protocols: ['https'],
    require_protocol: true,
    /* eslint-enable */
  });

/**
 * Check if url matches Smartcar's allowed custom scheme
 *
 * @throws invalidCustomScheme if url does not match Smartcar's custom scheme
 */
helpers.validateCustomScheme = url => {
  if (
    !validator.isURL(url.href, {
      /* eslint-disable camelcase */
      require_protocol: true,
      require_valid_protocol: false,
      require_tld: false,
      /* eslint-enable camelcase */
    })
  ) {
    throw new Error(errorMessages.invalidProtocol);
  }
};

/**
 * Check if pathname of url begins with '/v{number}/redirect' or '/redirect'
 *
 * @param {URL} url - URL to check
 * @returns {boolean} whether or not pathname of url begins with '/v{number}/redirect'
 * or '/redirect'
 */
helpers.hasValidPath = url =>
  /^\/v\d+\/redirect.*/u.test(url.pathname) ||
  url.pathname.startsWith('/redirect');

/**
 * Validate that url has expected query parameters (only `app_origin`)
 *
 * @param {URL} url - URL to check
 */
helpers.validateQueryParams = url => {
  if (!url.searchParams.has('app_origin')) {
    throw new Error(errorMessages.missingAppOrigin);
  } else if (Array.from(url.searchParams).length !== 1) {
    throw new Error(errorMessages.extraQueryParams);
  }
};

module.exports = helpers;
